import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Spinner } from '@iaa-di/spinner'
import { useAtomValue, useSetAtom } from 'jotai'
import { UAParser } from 'ua-parser-js'

import IAALogo from '@/assets/IAA_Logo.jpg'
import { LoadingCard } from '@/components/LoadingCard'
import { FieldError } from '@/components/FieldError'
import { fieldInputClassName } from '@/helpers/fieldInputClassName'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { TestIds } from '@/constants/testIds'
import {
  loadableLoginAtom,
  loginRequestAtom,
  initialWorkOrderIdAtom,
  loggedInEmailAtom,
  emailLinkWorkOrderIdAtom,
} from '@/state/authentication/login'
import { readOnlyRequestAtom } from '@/state/dashboard'
import { RegistrationStatus } from '@/types/'
import type { LoginRequest } from '@/types/authentication'
import { getErrorMessage } from '@/helpers/login-helpers'
import { LoginSchema } from '@/schema'
import { ClarityEvent, trackClarityEvent } from '@/services/telemetry/clarity'

export const Route = createFileRoute('/login')({
  component: Login,
})

function Login() {
  useDocumentTitle('meta.title.login')
  const intl = useIntl()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSessionExpired] = useState(
    () => sessionStorage.getItem('sessionTimeout') === 'true',
  )
  const [validationErrors, setValidationErrors] = useState<{
    email?: string
    password?: string
  }>({})
  const setLoginRequest = useSetAtom(loginRequestAtom)
  const loginAtom = useAtomValue(loadableLoginAtom)
  const setInitialWorkOrderId = useSetAtom(initialWorkOrderIdAtom)
  const setLoggedInEmail = useSetAtom(loggedInEmailAtom)
  const setReadOnlyRequest = useSetAtom(readOnlyRequestAtom)
  const emailLinkWorkOrderId = useAtomValue(emailLinkWorkOrderIdAtom)
  const data = loginAtom.state === 'hasData' ? loginAtom.data : null
  const isLoading = loginAtom.state === 'loading'
  const errorMessage = getErrorMessage(loginAtom)

  useEffect(() => {
    sessionStorage.removeItem('sessionTimeout')
  }, [])

  useEffect(() => {
    if (
      loginAtom.state === 'hasData' &&
      data?.isSuccess &&
      data?.data?.workOrderId
    ) {
      setLoginRequest(null)
      // Use the email-link WO when available — it is the one the user clicked through
      const targetWO =
        emailLinkWorkOrderId != null && emailLinkWorkOrderId > 0
          ? emailLinkWorkOrderId
          : data.data.workOrderId
      // Reset then set so the email-link WO always wins over stale cached values
      setInitialWorkOrderId(null)
      setInitialWorkOrderId(targetWO)
      setLoggedInEmail(email)
      setReadOnlyRequest(null)
      sessionStorage.setItem('isSignedIn', 'true')
      if (
        data.registrationStatus === RegistrationStatus.RegisteredNotVerified
      ) {
        trackClarityEvent(ClarityEvent.VerificationRequired)
        navigate({ to: '/verifyInfo' })
      } else if (
        data.registrationStatus === RegistrationStatus.RegisteredVerified
      ) {
        trackClarityEvent(ClarityEvent.VerificationNotRequired)
        navigate({ to: '/dashboard' })
      }
    }
  }, [
    loginAtom,
    data,
    email,
    navigate,
    setLoggedInEmail,
    setReadOnlyRequest,
    setInitialWorkOrderId,
    setLoginRequest,
    emailLinkWorkOrderId,
  ])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (validationErrors.email)
      setValidationErrors((prev) => ({ ...prev, email: undefined }))
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (validationErrors.password)
      setValidationErrors((prev) => ({ ...prev, password: undefined }))
  }

  const handlePasswordBlur = () => {
    if (password.length > 0 && password.length < 8) {
      setValidationErrors((prev) => ({
        ...prev,
        password: 'login.validation.passwordLength',
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationResult = LoginSchema.safeParse({ email, password })
    if (!validationResult.success) {
      const fieldErrors = validationResult.error.flatten().fieldErrors
      setValidationErrors({
        email: fieldErrors.email?.[0],
        password: fieldErrors.password?.[0],
      })
      return
    }
    setValidationErrors({})

    const parser = new UAParser()
    const result = parser.getResult()

    const request: LoginRequest = {
      emailAddress: email,
      password: password,
      browser: (result.browser && JSON.stringify(result.browser)) || 'Unknown',
      operatingSystem: (result.os && JSON.stringify(result.os)) || 'Unknown',
      device: (result.device && JSON.stringify(result.device)) || 'Unknown',
    }

    setLoginRequest(request)
  }

  return (
    <div className="flex items-center justify-center overflow-y-auto mt-2">
      <div className="w-full max-w-md space-y-8 px-4">
        <p className="block text-xl font-bold mb-2">
          <FormattedMessage id="login.myVehicleClaim" />
        </p>
        <p className="text-sm flex items-center gap-1">
          <FormattedMessage id="login.productBy" />{' '}
          <img src={IAALogo} className="h-4 inline-block" alt="IAA logo" />
        </p>
        <h1 className="block text-sm font-small mb-4">
          <FormattedMessage id="login.signIn" />
        </h1>

        {isSessionExpired && (
          <div
            role="alert"
            className="rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900"
          >
            <FormattedMessage id="login.sessionExpired" />
          </div>
        )}

        <hr className="border-0 h-2 w-1/3 bg-red-600" />

        <form onSubmit={handleSubmit} className="space-y-6">
          {isLoading && (
            <div
              data-testid={TestIds.login.loadingContainer}
              className="flex items-center justify-center py-12"
            >
              <div className="relative">
                <LoadingCard short={true} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Spinner />
                </div>
              </div>
            </div>
          )}

          {!isLoading && (
            <>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mt-2"
                >
                  <FormattedMessage id="login.email" />
                </label>
                <input
                  data-testid={TestIds.login.emailInput}
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={intl.formatMessage({ id: 'login.email' })}
                  aria-invalid={!!validationErrors.email}
                  aria-describedby={
                    validationErrors.email ? 'email-error' : undefined
                  }
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!validationErrors.email)}`}
                />
                <FieldError
                  id="email-error"
                  messageId={validationErrors.email}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium">
                  <FormattedMessage id="login.password" />
                </label>
                <input
                  data-testid={TestIds.login.passwordInput}
                  id="password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  maxLength={30}
                  placeholder={intl.formatMessage({ id: 'login.password' })}
                  aria-invalid={!!validationErrors.password}
                  aria-describedby={
                    validationErrors.password ? 'password-error' : undefined
                  }
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!validationErrors.password)}`}
                />
                <FieldError
                  id="password-error"
                  messageId={validationErrors.password}
                />
              </div>
            </>
          )}

          {loginAtom.state === 'hasData' && !isLoading && errorMessage && (
            <div
              data-testid={TestIds.login.errorMessage}
              role="alert"
              className="rounded bg-orange-500 p-4 text-sm font-semibold text-white"
            >
              <FormattedMessage id={errorMessage} />
            </div>
          )}

          <button
            type="submit"
            data-testid={TestIds.login.signInButton}
            disabled={isLoading}
            className="w-full rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <FormattedMessage id="login.signingIn" />
            ) : (
              <FormattedMessage id="login.signIn" />
            )}
          </button>

          <div
            className="text-center"
            data-testid={TestIds.login.forgotPasswordLink}
          >
            <Link
              disabled={isLoading}
              to="/resetPasswordRequest"
              className="!text-black !underline"
            >
              <FormattedMessage id="login.forgotPassword" />
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
